import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaShieldAlt } from 'react-icons/fa';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { getTutorialVoicePath, stopTutorialVoice, playTutorialVoice } from '../tutorial/tutorialVoiceMap';
import './TutorialDialog.css';

const DEFAULT_TUTORIAL_IMAGE = '/images/commander-close.png';

// Shield icon: empty circle (matches AbilityIndicator.js EmptyCircle for Shield ability)
const ShieldIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '2px' }}>
    <circle cx="12" cy="12" r="10" />
  </svg>
);

// Parse tutorial text: replace [Protect] and [Shield] with actual ability icons from order design (AbilityIndicator.js)
// Also handle newlines for paragraph breaks
function renderTutorialTextWithIcons(text) {
  if (!text) return null;
  const paragraphs = text.split('\n');
  return paragraphs.map((para, pIdx) => {
    const parts = para.split(/(\[Protect\]|\[Shield\])/);
    const content = parts.map((part, i) => {
      if (part === '[Protect]') {
        return <span key={i} className="tutorial-ability-icon" title="Protect"><FaShieldAlt size={18} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '2px' }} /></span>;
      }
      if (part === '[Shield]') {
        return <span key={i} className="tutorial-ability-icon" title="Shield"><ShieldIcon size={18} /></span>;
      }
      return part;
    });
    return <React.Fragment key={pIdx}>{pIdx > 0 && <br />}{content}</React.Fragment>;
  });
}

/**
 * Simple Tutorial Dialog
 * In-game campaign tutorials - upper right corner with image half inside / half outside
 * Completely independent dialog rendered via portal
 * Does not cause any re-renders of BattleUI
 */
const SimpleTutorialDialog = ({ explanation, missionId, onContinue, hideContinueButton = false, topOffset = 0, voiceKey }) => {
  const { t } = useTranslation();
  const contentColumnRef = useRef(null);
  const voiceAudioRef = useRef(null);

  // Play voice when this message is shown; stop when leaving or when message changes (no overlap)
  useEffect(() => {
    const key = voiceKey ?? (explanation?.stepId || explanation?.title);
    const path = key ? getTutorialVoicePath(key) : null;
    stopTutorialVoice(voiceAudioRef);
    voiceAudioRef.current = null;
    if (path) {
      playTutorialVoice(path, voiceAudioRef);
    }
    return () => {
      stopTutorialVoice(voiceAudioRef);
      voiceAudioRef.current = null;
    };
  }, [voiceKey, explanation?.stepId, explanation?.title]);

  useEffect(() => {
    if (explanation && contentColumnRef.current) {
      contentColumnRef.current.scrollTop = 0;
    }
  }, [explanation?.stepId, explanation?.title, explanation?.text]);

  if (!explanation) {
    return null;
  }

  const stepId = explanation.stepId;
  const title = missionId && stepId
    ? (t(`campaign.missions.${missionId}.steps.${stepId}.title`, { defaultValue: '' }) || explanation.title?.trim() || '')
    : (explanation.title?.trim() || '');
  const text = missionId && stepId
    ? (t(`campaign.missions.${missionId}.steps.${stepId}.text`, { defaultValue: '' }) || explanation.text?.trim() || '')
    : (explanation.text?.trim() || '');
  const image = explanation.image || DEFAULT_TUTORIAL_IMAGE;

  return createPortal(
    <div className="tutorial-overlay" style={topOffset ? { top: `${20 + topOffset}px` } : undefined}>
      <div
        className="tutorial-dialog tutorial-dialog-with-image"
        style={hideContinueButton ? { pointerEvents: 'none' } : undefined}
      >
        {image && (
          <div className="tutorial-dialog-image-column">
            <div className="tutorial-dialog-image-container">
              <img src={image} alt="" className="tutorial-dialog-image" />
            </div>
          </div>
        )}
        <div className="tutorial-dialog-content-column" ref={contentColumnRef}>
          {title && (
            <div className="tutorial-header">
              <h2>{title}</h2>
            </div>
          )}

          {text && (
            <div className="tutorial-content">
              <p>{renderTutorialTextWithIcons(text)}</p>
            </div>
          )}

          {!hideContinueButton && (
            <div className="tutorial-action-buttons">
              <button
                className="tutorial-continue-button"
                onClick={() => {
                  playSoundEffect('/sounds/click.mp3');
                  onContinue();
                }}
                autoFocus
              >
                {t('welcome.tutorial.continue')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SimpleTutorialDialog;

